/**
 * =============================================================================
 * ROASTBREW - PLAYBACK CAPABILITY CONTEXT
 * =============================================================================
 *
 * Computes the playback-capability verdict ONCE, at the top of the app, and
 * shares it so any page can read it without recomputing. This is the React
 * wrapper around utils/playbackCapabilityDetection.js — all real logic lives
 * in that pure module; this file is only the provider + hook.
 *
 * USAGE (route-level pages + the video router):
 *   import { usePlaybackCapability } from './PlaybackCapabilityContext';
 *   const { player, drm, device } = usePlaybackCapability();
 *
 * PROVIDED VALUE (frozen, computed once):
 *   { drm, player, device, isAppleMobile, override, hasFairPlay }
 *   - player: 'videojs' | 'shaka' | 'unsupported'  ← the routing decision
 *   - drm:    'fairplay' | 'widevine' | 'none'
 *   - device: 'phone' | 'tablet' | 'desktop'       ← redirect COPY only, never routes
 *
 * PLACEMENT: mounted OUTERMOST, wrapping <AppInner/> in App.jsx, above all auth
 * state. It depends on nothing (pure capability probes), so everything can
 * depend on it and it can race with nothing. Computed in useMemo([]) so it runs
 * on the first synchronous render pass and never recomputes.
 *
 * StrictMode note: in development React double-invokes render (and useMemo) to
 * surface impure code. resolvePlaybackCapability() is pure, so this is harmless —
 * the debug log below may print twice in dev, once in production. Expected.
 * =============================================================================
 */

import React, { createContext, useContext, useMemo } from 'react';
import { resolvePlaybackCapability } from './utils/playbackCapabilityDetection';

const PlaybackCapabilityContext = createContext(null);

export function PlaybackCapabilityProvider({ children }) {
  // Computed once on first render. Capability cannot change mid-session, so the
  // empty dep array is correct — no page ever recomputes it.
  const value = useMemo(() => resolvePlaybackCapability(), []);

  // Diagnostic: open any page and confirm the verdict in the console. Kept
  // OUTSIDE the useMemo so StrictMode's double-render can't be mistaken for a
  // recompute of the value itself.
  console.log('[playback-capability]', value);

  return (
    <PlaybackCapabilityContext.Provider value={value}>
      {children}
    </PlaybackCapabilityContext.Provider>
  );
}

export function usePlaybackCapability() {
  const ctx = useContext(PlaybackCapabilityContext);
  if (!ctx) {
    throw new Error('usePlaybackCapability must be used within a PlaybackCapabilityProvider');
  }
  return ctx;
}

export default PlaybackCapabilityContext;
